/**
 * Mirror of the backend `AttachmentRedactionStatus` enum
 * (ICS.Domain.Shared/Modules/Attachment/AttachmentRedactionStatus.cs).
 * The API serializes enums as name strings; the numeric map below is a
 * defensive fallback in case a surface returns the raw int.
 */
export enum ATTACHMENT_REDACTION_STATUS {
  None = 'None',
  Pending = 'Pending',
  Processing = 'Processing',
  Redacted = 'Redacted',
  Unsupported = 'Unsupported',
  Failed = 'Failed',
  NeedsReview = 'NeedsReview',
}

const NUMERIC_STATUS_MAP: Record<number, ATTACHMENT_REDACTION_STATUS> = {
  0: ATTACHMENT_REDACTION_STATUS.None,
  1: ATTACHMENT_REDACTION_STATUS.Pending,
  2: ATTACHMENT_REDACTION_STATUS.Processing,
  3: ATTACHMENT_REDACTION_STATUS.Redacted,
  4: ATTACHMENT_REDACTION_STATUS.Unsupported,
  5: ATTACHMENT_REDACTION_STATUS.Failed,
  6: ATTACHMENT_REDACTION_STATUS.NeedsReview,
}

export function normalizeRedactionStatus(
  value: unknown
): ATTACHMENT_REDACTION_STATUS {
  if (typeof value === 'number') {
    return NUMERIC_STATUS_MAP[value] ?? ATTACHMENT_REDACTION_STATUS.None
  }
  if (typeof value === 'string' && value in ATTACHMENT_REDACTION_STATUS) {
    return ATTACHMENT_REDACTION_STATUS[
      value as keyof typeof ATTACHMENT_REDACTION_STATUS
    ]
  }
  return ATTACHMENT_REDACTION_STATUS.None
}

/** antd Tag color + label per status (None renders nothing). */
export const REDACTION_STATUS_TAG: Record<
  ATTACHMENT_REDACTION_STATUS,
  { color: string; label: string } | null
> = {
  [ATTACHMENT_REDACTION_STATUS.None]: null,
  [ATTACHMENT_REDACTION_STATUS.Pending]: { color: 'blue', label: 'Pending' },
  [ATTACHMENT_REDACTION_STATUS.Processing]: {
    color: 'blue',
    label: 'Processing',
  },
  [ATTACHMENT_REDACTION_STATUS.Redacted]: { color: 'green', label: 'Redacted' },
  [ATTACHMENT_REDACTION_STATUS.Unsupported]: {
    color: 'orange',
    label: 'Not supported',
  },
  [ATTACHMENT_REDACTION_STATUS.Failed]: { color: 'red', label: 'Failed' },
  [ATTACHMENT_REDACTION_STATUS.NeedsReview]: {
    color: 'volcano',
    label: 'Needs review',
  },
}

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

export const REDACTION_PLACEHOLDER_MESSAGE =
  'Not redacted — the original document is hidden.'

/**
 * True when this row is a redaction placeholder returned to offshore staff:
 * the backend blanks the file id for Unsupported/Failed/NeedsReview attachments
 * so the document is visible as metadata only (no view/download).
 */
const PLACEHOLDER_STATUSES: ATTACHMENT_REDACTION_STATUS[] = [
  ATTACHMENT_REDACTION_STATUS.Unsupported,
  ATTACHMENT_REDACTION_STATUS.Failed,
  ATTACHMENT_REDACTION_STATUS.NeedsReview,
]

export function isRedactionPlaceholder(file: {
  fileId?: string | null
  redactionStatus?: unknown
}): boolean {
  if (!file) {
    return false
  }

  const status = normalizeRedactionStatus(file.redactionStatus)
  if (!PLACEHOLDER_STATUSES.includes(status)) {
    return false
  }

  return !file.fileId || file.fileId === EMPTY_GUID
}
